import { compose } from "redux";
import { connect } from "react-redux";
import React, { useEffect, useState } from "react";

import { withStyles } from "@material-ui/core/styles";
import {
  Badge,
  Button,
  Collapse,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  SvgIcon,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import FilterIcon from "@material-ui/icons/FilterList";
import DeleteIcon from "@material-ui/icons/Delete";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import RefreshIcon from "@material-ui/icons/Refresh";

import DataGrid, { Column, HeaderFilter, Paging, SearchPanel } from "devextreme-react/data-grid";
import FilterBuilder, { CustomOperation } from 'devextreme-react/filter-builder';
import TagBox from 'devextreme-react/tag-box';

import { format as dateFormat, add } from 'date-fns';

import {
  appNotify,
  fetchRegions,
  fetchStrategies,
  setDashboardFilter,
  setDashboardRules,
  setSavedFilters,
  toggleDashboardFilterEditor
} from "../../../store/actions";
import { userRoleUnderwriteManagerSelector } from "../../../store/selectors";
import { GetSavedFilters, PostSavedFilter, PutSavedFilter } from "./FilterData";
import Stack from "../../layout/Stack";
import { sendClientLog } from "../../../services/logService";
import ModalPage from "../../explore/layout/ModalPage";

const pageSize = 10;
const APPLY = 'Apply';
const percentFormat = { precision: 2, type: 'percent' };

function FilterEditor ({
  classes,
  filterEditorOpen,
  toggleDashboardFilterEditor,
  setDashboardFilter,
  setDashboardRules,
  filter,
  rules,
  savedFilters,
  currentUser,
  setSavedFilters,
  userHasUnderwriteManagerRole,
  appNotify,
  refreshWorkloadData,
  fetchRegions,
  fetchStrategies,
  dashboardRegions,
  dashboardStrategies
}) {
  const [saveFilterExpanded, setSaveFilterExpanded] = useState(false);
  const [filterNameInput, setFilterNameInput] = useState('');
  const [filterNameError, setFilterNameError] = useState('');
  const [viewType, setViewType] = useState('public');
  const [tmpRules, setTmpRules] = useState(rules);
  const [tmpFilter, setTmpFilter] = useState(null);

  useEffect(() => {
    if (filterEditorOpen) {
      fetchFilterData();

      // fetch regions and strategies if they haven't fetched yet
      !dashboardRegions.length && fetchRegions();
      !dashboardStrategies.length && fetchStrategies();
    }
  }, [filterEditorOpen]);

  function customizeText(dateObject) {
    return dateObject?.valueText
      ? dateFormat(new Date(dateObject.valueText), 'M/d/yyyy')
      : dateObject?.valueText;
  }

  function convertDateStringToDateFormat(value) {
    return typeof value === 'string' ? new Date(value) : value;
  }

  function getFilterExpressionByRange (filterValue) {
    let startFilterExpression;
    let endFilterExpression;
    const selector = 'ImportedOn';
    if (Array.isArray(filterValue) && filterValue[0] && filterValue[1]) {
      const filterValue0 = convertDateStringToDateFormat(filterValue[0]);
      const filterValue1 = convertDateStringToDateFormat(filterValue[1]);
      const startDate = dateFormat(filterValue0, 'MM/dd/yyyy');
      const endDate = dateFormat(add(filterValue1, { days: 1 }), 'MM/dd/yyyy');
      startFilterExpression = [selector, '>=', startDate];
      endFilterExpression = [selector, '<', endDate];

      return [startFilterExpression, "and", endFilterExpression]
    }
  }

  function calculateFilterExpression(filterValue, selectedFilterOperation) {
    const selector = 'ImportedOn';
    if (selectedFilterOperation === 'between') {
      return getFilterExpressionByRange(filterValue);
    }

    if (filterValue === null) {
      return [selector, selectedFilterOperation || '=', null];
    }

    const date = convertDateStringToDateFormat(filterValue);
    const dateStart = dateFormat(date, 'MM/dd/yyyy');
    const dateEnd = dateFormat(add(date, { days: 1 }), 'MM/dd/yyyy');

    switch (selectedFilterOperation) {
      case "<":
        return [selector, "<", dateStart];
      case "<=":
        return [selector, "<", dateEnd];
      case ">":
        return [selector, ">=", dateEnd];
      case ">=":
        return [selector, ">=", dateStart];
      case "<>":
        return [
          [selector, "<", dateStart], "or", [selector, ">=", dateEnd]
        ];
      default:
        return [
          [selector, ">=", dateStart], "and", [selector, "<", dateEnd]
        ]
    }
  }

  const fields = [
    {
      caption: 'Import Date',
      dataField: 'ImportedOn',
      dataType: 'date',
      customizeText,
      calculateFilterExpression,
    }, {
      caption: 'Rental TBPP',
      dataField: 'TargetBidPriceRentalPercentOfPrice',
      dataType: 'number',
      format: percentFormat,
    }, {
      caption: 'Retail TBPP',
      dataField: 'TargetBidPriceRetailPercentOfPrice',
      dataType: 'number',
      format: percentFormat,
    }, {
      caption: 'Asking Price',
      dataField: 'AskingPrice',
      dataType: 'number',
      format: 'currency'
    }, {
      caption: 'Amherst Region',
      dataField: 'Region',
      dataType: 'string',
      filterOperations: ["contains", "startswith", "=", "<>", "isblank", "isnotblank", "anyof", ],
      lookup: {
        dataSource: dashboardRegions
      },
    }, {
      caption: 'Strategy',
      dataField: 'DescriptiveFitOrderNames',
      dataType: 'string',
      filterOperations: ["contains", "startswith", "=", "<>", "isblank", "isnotblank", "anyof", ],
      lookup: {
        dataSource: dashboardStrategies
      }
    }
  ];

  const CommandColumnRender = (e) => {
    return (
      <Stack horizontal>
        <Tooltip title={APPLY} placement="bottom">
          <SvgIcon
            onClick={() => { applySavedFilterClick(e) }}
            className={classes.btnApply}
          >
            <path d="M19.78,2.2L24,6.42L8.44,22L0,13.55L4.22,9.33L8.44,13.55L19.78,2.2M19.78,5L8.44,16.36L4.22,12.19L2.81,13.55L8.44,19.17L21.19,6.42L19.78,5Z" />
          </SvgIcon>
        </Tooltip>
        <Tooltip title={"Apply & Close"} placement="bottom">
          <SvgIcon
            onClick={() => { applyAndCloseSavedFilterClick(e) }}
            className={classes.btnApply}
          >
            <path
              d="M19.78,2.2L24,6.42L8.44,22L0,13.55L4.22,9.33L8.44,13.55L19.78,2.2M19.78,5L8.44,16.36L4.22,12.19L2.81,13.55L8.44,19.17L21.19,6.42L19.78,5Z
                        M14.46,15.88L15.88,14.46L18,16.59L20.12,14.46L21.54,15.88L19.41,18L21.54,20.12L20.12,21.54L18,19.41L15.88,21.54L14.46,20.12L16.59,18L14.46,15.88"
            />
          </SvgIcon>
        </Tooltip>
      </Stack>
    );
  };

  const deleteColumnRender = e => {
    if (e.data.canDelete === false) return null;
    return (
      <Tooltip title={"Delete"} placement="bottom">
        <IconButton
          style={{
            padding: 2,
            margin: -2
          }}
          onClick={() => handleFilterDelete(e)}
        >
          <DeleteIcon />
        </IconButton>
      </Tooltip>
    )
  }

  function onFilterOpenClick() {
    toggleDashboardFilterEditor(true);
  }

  const onClose = () => {
    toggleDashboardFilterEditor(false);
  };

  function onApplyFilterClick() {
    setDashboardFilter(tmpFilter);
    setDashboardRules(tmpRules);
    refreshWorkloadData();
  }

  function onApplyAndCloseFilterClick() {
    setDashboardFilter(tmpFilter);
    setDashboardRules(tmpRules);
    onClose();
    refreshWorkloadData();
  }

  function onClearFilterClick() {
    setDashboardFilter(null);
    setDashboardRules(null);
    refreshWorkloadData();
  }

  function fetchFilterData() {
    GetSavedFilters(currentUser.id, userHasUnderwriteManagerRole).then(filters => {
      console.log('Saved filters: ', filters);
      setSavedFilters(filters);
    });
  }

  function applySavedFilterClick(e) {
    applySavedFilter(e);
  }

  function applyAndCloseSavedFilterClick(e) {
    applySavedFilter(e);
    onClose();
  }

  function applySavedFilter(e) {
    if (!e?.row?.data?.id) {
      return;
    }

    const selectedFilter = savedFilters.find((f) => f.id === e.row.data.id);
    if (!selectedFilter) {
      return;
    }

    setDashboardFilter(selectedFilter.filter);
    setDashboardRules(selectedFilter.rules);
    refreshWorkloadData();
  }

  function handleFilterDelete(e) {
    const { id, name, viewType, filter, rules, userId } = e.row.data;
    const request = {
      id,
      name,
      viewType: getViewTypeEnum(viewType.toLowerCase()),
      rules: rules ? JSON.stringify(rules) : null,
      filter: filter ? JSON.stringify(filter) : null,
      deleted: true,
      userId,
    }

    PutSavedFilter(request, currentUser.id).then(res => {
      console.log('Filter deleted: ' + res);
      appNotify('Filter deleted', 'success');

      fetchFilterData();
      sendClientLog('Info', `Dashboard - User deleted saved filter with id ${id}`);
    }).catch(error => {
      console.log(error)
      appNotify('Error while deleting filter', 'error');
    });
  }

  function handleFilterNameInputChange(e) {
    if (filterNameError?.length) {
      setFilterNameError('');
    }

    setFilterNameInput(e.target.value);
  }

  function onSaveNewFilterClick(e) {
    if (filterNameInput === '') {
      setFilterNameError('Filter name is required');
      return;
    }

    const request = {
      name: filterNameInput,
      viewType: getViewTypeEnum(viewType),
      rules: rules ? JSON.stringify(rules) : null,
      filter: filter ? JSON.stringify(filter) : null,
      deleted: false,
      userId: currentUser.id,
    }

    PostSavedFilter(request).then(response => {
      console.log('Filter saved: ' + response);
      appNotify('Filter saved', 'success');
      setFilterNameInput('');
      setViewType('public');

      fetchFilterData();
      sendClientLog('Info', `Dashboard - User created saved filter with id ${response}`);
    }).catch(error => {
      console.error(error);
      if (error.message) {
        const message = JSON.parse(error.message);
        if (message?.filterName?.find(el => el.includes('already exists'))) {
          console.log('name already exists')
          setFilterNameError('A filter with this name already exists');
        }
      }
      appNotify('Error while saving filter', 'error');
    });
  }

  function handleViewTypeChange(e) {
    setViewType(e.target.value);
  }

  function getViewTypeEnum(value) {
    const viewType = {
      public: 0,
      private: 1
    };

    return viewType[value];
  }

  function getSavedFiltersData(savedFilters) {
    return savedFilters.map(filter => {
      const viewType = filter.viewType === 0 ? 'Public' : 'Private';
      return {
        ...filter,
        viewType,
      }
    }).filter(filter => {
      return filter.viewType === 'Public' || filter.userId === currentUser.id;
    });
  }

  function onFilterChange(e) {
    setTmpFilter(e.component.getFilterExpression());
    setTmpRules(e.value);
  }

  const getFilterBuilderCustomOperations = () => {
    const operations = [
      {
        name: 'anyof',
        caption: 'Is any of',
        dataTypes: ['string'],
        icon: "check",
        editorRender: props =>
          <TagBox
              width={300}
              defaultValue={props.value}
              items={props.field.lookup.dataSource}
              onValueChanged={e => { props.setValue(e.value && e.value.length ? e.value : null); }}
          />,
        calculateFilterExpression: (filterValue, field) => {
          const filter =
            filterValue
            && filterValue.length
            && Array.prototype.concat.apply([], filterValue.map(function(value) {
              return [[field.dataField, '=', value], 'or'];
            })).slice(0, -1);
          return filter;
        },
      }, {
        name: 'contains',
        caption: 'Contains',
        dataTypes: ['string'],
        icon: "contains",
        editorRender: props =>
          <TextField
            defaultValue={props.value}
            inputProps={{ style: { fontSize: 13, padding: '0 0 5px 0' } }}
            onChange={e => { props.setValue(e.target.value && e.target.value.length ? e.target.value : null); }}
          />,
        calculateFilterExpression: (filterValue, field) => {
          return  filterValue && [[field.dataField, 'contains', filterValue]];
        },
      }, {
        name: 'startswith',
        caption: 'Starts with',
        dataTypes: ['string'],
        icon: "startswith",
        editorRender: props =>
          <TextField
            defaultValue={props.value}
            inputProps={{ style: { fontSize: 13, padding: '0 0 5px 0' } }}
            onChange={e => { props.setValue(e.target.value && e.target.value.length ? e.target.value : null); }}
          />,
        calculateFilterExpression: (filterValue, field) => {
          return  filterValue && [[field.dataField, 'startswith', filterValue]];
        },
      },
    ];

    return operations.map(operation => {
      return <CustomOperation
        key={operation.name}
        name={operation.name}
        caption={operation.caption}
        dataTypes={operation.dataTypes}
        hasValue={operation.hasValue}
        editorRender={operation.editorRender}
        calculateFilterExpression={operation.calculateFilterExpression}
        icon={operation.icon}
      />
    })
  };

  return(
    <>
      <ModalPage
        maxWidth="md"
        title="Filters"
        open={filterEditorOpen}
        onClose={onClose}
        disableEnforceFocus
        additionalActions={[
          {
            key: "btnApply",
            text: APPLY,
            onClick: onApplyFilterClick,
            color: "primary",
          },
          {
            key: "btnApplyAndClose",
            text: "Apply & Close",
            onClick: onApplyAndCloseFilterClick,
            color: "primary",
          },
          {
            key: "btnClear",
            text: "Clear",
            onClick: onClearFilterClick,
            color: "secondary",
          },
        ]}
      >
        <Typography variant="subtitle1">Current Filter</Typography>
        <div className={classes.queryBuilderWrapper}>
          <FilterBuilder
            key={rules?.toString()}
            fields={fields}
            defaultValue={rules}
            onValueChanged={onFilterChange}
          >
            {getFilterBuilderCustomOperations()}
          </FilterBuilder>
        </div>
        <div className={classes.spacer} />
        <div style={{display: 'flex'}}>
          <Typography variant="subtitle1" style={{zIndex: 1, display: "inline-block"}}>Saved Filters</Typography>
          <IconButton
            className={classes.refreshButton}
            onClick={fetchFilterData}
            style={{zIndex: 1}}
          >
            <RefreshIcon />
          </IconButton>
        </div>

        <DataGrid
          id='savedFilterGrid'
          keyExpr={'id'}
          dataSource={getSavedFiltersData(savedFilters)}
          showBorders={true}
          height={434}
        >

          <Column width={75} cellRender={CommandColumnRender} />
          <Column dataField={"name"} />
          <Column dataField={"createdOn"} dataType={"date"} sortOrder={"desc"} format={"MM/dd/yyyy"} width={120} />
          <Column dataField={"userName"} width={140} />
          <Column dataField={"viewType"} width={110} allowFiltering={true} />
          <Column cellRender={deleteColumnRender} alignment='center' width={48} />

          <Paging defaultPageSize={pageSize} />
          <HeaderFilter visible={true} />
          <SearchPanel visible={true} searchVisibleColumnsOnly={true} />

        </DataGrid>

        <div className={classes.saveNewFilterCaption}>
          <Typography variant="subtitle1">Save New Filter</Typography>
          <IconButton
            className={classes.expandButton}
            style={{transform: `rotate(${saveFilterExpanded ? '180' : '0'}deg)`,}}
            onClick={() => setSaveFilterExpanded(!saveFilterExpanded)}
          >
            <ExpandMoreIcon />
          </IconButton>
        </div>
        <Collapse in={saveFilterExpanded}>
          <div style={{display: 'flex', alignItems: 'center', border: '1px solid #e0e0e0', padding: '8px 8px 4px'}}>
            <TextField
              label='Filter Name'
              placeholder='Enter a name...'
              onChange={handleFilterNameInputChange}
              value={filterNameInput}
              style={{width: 346, height: 68, marginRight: 16}}
              InputLabelProps={{
                shrink: true,
              }}
              error={filterNameError?.length > 0}
              helperText={filterNameError}
            />
            <FormControl component="fieldset" style={{marginRight: 32, marginBottom: 12}}>
              <FormLabel component="legend">View Type</FormLabel>
              <RadioGroup row aria-label="viewType" name="viewType" value={viewType} onChange={handleViewTypeChange} style={{flexWrap: 'nowrap'}}>
                <FormControlLabel value="public" control={<Radio size='small'/>} label="Public" />
                <FormControlLabel value="private" control={<Radio size='small'/>} label="Private" />
              </RadioGroup>
            </FormControl>
            <div style={{flexGrow: 1, textAlign: 'end'}}>
              <Typography variant='caption'>
                {!rules ? 'Apply a data filter to enable saving.' : ''}
              </Typography>
            </div>
            <Button
              variant='outlined'
              color='primary'
              onClick={onSaveNewFilterClick}
              disabled={filterNameError?.length || !rules}
              style={{margin: '0 8px'}}
            >
              Save
            </Button>
          </div>
        </Collapse>
      </ModalPage>

      <Paper className={classes.root}>
        <Tooltip title={`Data Filters`} placement="bottom">
              <span>
                <IconButton
                  className={classes.actionButton}
                  onClick={onFilterOpenClick}
                >
                  {
                    rules ?
                      <Badge className={classes.margin} color="secondary" variant="dot" overlap='rectangular'>
                        <FilterIcon />
                      </Badge>
                      :
                      <FilterIcon />
                  }
                </IconButton>
              </span>
        </Tooltip>
      </Paper>
    </>
  );
}

const styles = ({
  root: {
    width: '48px',
    height: '48px',
  },
  refreshButton: {
    padding: 2,
    marginLeft: 6
  },
  spacer: {
    height: 16,
  },
  queryBuilderWrapper: {
    border: "1px solid #e0e0e0",
    padding: 10
  },
  btnApply: {
    marginRight: 10,
    padding: 2,
    background: "rgba(0, 0, 0, 0.1)",
    borderRadius: `5px`,
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.3)",
      cursor: `pointer`,
    },
  },
  saveNewFilterCaption: {
    display: 'flex',
    alignItems: 'center',
  }
});

const mapState = (state) => ({
  filterEditorOpen: state.dashboardState.filterEditorOpen,
  filter: state.dashboardState.filter,
  rules: state.dashboardState.rules,
  savedFilters: state.dashboardState.savedFilters,
  currentUser: state.appState.user,
  userHasUnderwriteManagerRole: userRoleUnderwriteManagerSelector(state),
  dashboardRegions: state.dashboardState.dashboardRegions,
  dashboardStrategies: state.dashboardState.dashboardStrategies,
});

const mapDispatch = (dispatch) => ({
  toggleDashboardFilterEditor: (state) => dispatch(toggleDashboardFilterEditor(state)),
  setDashboardFilter: (filter) => dispatch(setDashboardFilter(filter)),
  setDashboardRules: (filter) => dispatch(setDashboardRules(filter)),
  setSavedFilters: (filter) => dispatch(setSavedFilters(filter)),
  appNotify: (message, variant, options) => dispatch(appNotify(message, variant, options)),
  fetchRegions: () => dispatch(fetchRegions({})),
  fetchStrategies: () => dispatch(fetchStrategies({})),
});

export default compose(
  withStyles(styles),
  connect(mapState, mapDispatch)
)(FilterEditor);
